import { FileRecord } from '@approvalmax/ui/src/components';
import { hooks } from '@approvalmax/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useHistory } from 'react-router-dom';
import {
    CreateExternalPersonImage,
    DocumentType,
    ImageSide,
    useCreateExternalPersonImage,
    useUpdateExternalPerson,
} from 'shared/data/walletsKyc/v1';
import type { UnionToIntersection } from 'type-fest';
import { Path } from 'urlBuilder';
import { z } from 'zod';

import { stepsByInitialStep, wizardInitialValues, wizardStepSchemaMap } from './Wizard.constants';
import { excludeTimeFromDate } from './Wizard.helpers';
import { Step } from './Wizard.types';

export const useInitialStep = () => {
    const [initialStep] = hooks.useQueryParam<Step>('initialStep', Step.Details, { required: true });

    if ([Step.Details, Step.Document, Step.Selfie].includes(initialStep)) {
        return initialStep;
    }

    return Step.Details;
};

export const useSteps = () => {
    const initialStep = useInitialStep();

    return stepsByInitialStep[initialStep];
};

export const useActiveStep = () => {
    const initialStep = useInitialStep();

    return useState<Step>(initialStep);
};

export const useWizardStepIndex = (activeStep: Step, steps: Step[]) => {
    return useMemo(() => steps.findIndex((step) => step === activeStep), [activeStep, steps]);
};

export const useWizardRouting = () => {
    const [activeStep, setActiveStep] = useActiveStep();
    const steps = useSteps();
    const stepIndex = useWizardStepIndex(activeStep, steps);
    const isLastStep = steps.length - 1 === stepIndex;

    const moveStep = useCallback(
        (nextStepIndex: number) => {
            if (nextStepIndex >= 0 && steps.length - 1 >= nextStepIndex) setActiveStep(steps[nextStepIndex]);
        },
        [setActiveStep, steps]
    );

    const moveForward = useCallback(() => {
        if (!isLastStep) {
            moveStep(stepIndex + 1);
        }

        return { isLastStep };
    }, [moveStep, stepIndex, isLastStep]);

    const moveBack = useCallback(() => {
        moveStep(stepIndex - 1);
    }, [moveStep, stepIndex]);

    return {
        step: activeStep,
        isFirstStep: stepIndex === 0,
        isLastStep,
        isSingleStep: steps.length === 1,
        moveForward,
        moveBack,
    };
};

export const useWizardForm = (step: Step, moveForward: ReturnType<typeof useWizardRouting>['moveForward']) => {
    const [documentType] = hooks.useQueryParam<DocumentType>('documentCategory');
    const [token] = hooks.useQueryParam('token');
    const accessToken = `Bearer ${token}`;
    const { push } = useHistory();

    const { mutateAsync: createExternalPersonImage } = useCreateExternalPersonImage({}, { accessToken });
    const { mutateAsync: updateExternalPerson } = useUpdateExternalPerson({}, { accessToken });

    const form = useForm<UnionToIntersection<z.infer<(typeof wizardStepSchemaMap)[Step]>>>({
        defaultValues: {
            person: {
                personDocuments: {
                    ...wizardInitialValues.personDocuments,
                    documentType,
                },
            },
        },
        resolver: zodResolver(wizardStepSchemaMap[step]),
    });

    const onSubmit = form.handleSubmit((values) => {
        const {
            person: { personDocuments, isUs, ...person },
        } = values;
        const { isLastStep } = moveForward();

        if (!isLastStep) {
            return;
        }

        // we have to use waterfall approach here because BE has a race condition on creating a new document on the
        // first uploaded image side
        return (Object.entries(values.person.personDocuments) as [ImageSide | 'documentType', FileRecord[]][])
            .reduce<CreateExternalPersonImage['body'][]>((acc, cur) => {
                const fileContent = cur[1][0]?.source;

                if (cur[0] !== 'documentType' && fileContent) {
                    acc.push({
                        documentType: values.person.personDocuments.documentType,
                        imageSide: cur[0],
                        fileName: fileContent.name,
                        fileContent,
                    });
                }

                return acc;
            }, [])
            .reduce((promise, body) => {
                promise = promise.then(() => createExternalPersonImage({ body }));

                return promise;
            }, Promise.resolve())
            .then(() => updateExternalPerson({ body: { ...person, dob: excludeTimeFromDate(person.dob) } }))
            .then(() => {
                push(`/${Path.walletsKycDetailsSubmitted}`);
            });
    });

    return {
        form,
        onSubmit,
        isLoading: form.formState.isSubmitting,
    };
};
